import type { FunctionComponent } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

import type { TemplateFunction } from 'Components/types';
import { toggle } from 'Core/actions/request';
import { createFitmentSearchResponseAllFacetsSelector } from 'Core/selectors/fitmentSearch/index';
import {
  createFacetSelector,
  searchQuerySelector,
  searchRequestSelectionSelector,
  totalHitsSelector,
} from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import type { SelectionEntry } from 'Models/index';
import { getItem, keys, setItem } from 'Utils/userStorage';

export type Params = {
  isToggled: boolean | null;
  toggleFacet: () => void;
};

export type FacetToggleConfig = {
  toggledFacet?: SelectionEntry;
  fieldsForVisibility?: Array<string>;
  initToggled?: boolean;
  isInverted?: boolean;
  saveLastState?: boolean;
};

export type Props = FacetToggleConfig & {
  template: TemplateFunction<Params>;
};

const FacetToggle: FunctionComponent<Props> = ({
  template,
  toggledFacet,
  fieldsForVisibility,
  initToggled,
  isInverted,
  saveLastState,
}) => {
  const dispatch = useDispatch();

  const facetToggleField = keys.facetToggleField(toggledFacet?.field ?? '');
  const [isToggled, setIsToggled] = useState(() =>
    saveLastState && getItem(facetToggleField, isFacetToggleStored) ? true : null,
  );

  const isVisible = useVisible(fieldsForVisibility, toggledFacet);

  const isToggledOnServer = useSelector(searchRequestSelectionSelector).some(
    (el) => !!toggledFacet && el.field === toggledFacet.field && el.term === toggledFacet.term,
  );

  useEffect(
    function updateUniversalBadgeVisibility() {
      if (toggledFacet?.field === 'Universal') {
        window.document.body.classList.toggle('cm_hide-universal-badge', !isVisible);
      }
    },
    [isVisible, toggledFacet?.field],
  );

  useEffect(() => {
    if (!toggledFacet || !isVisible) {
      return;
    }

    if (isToggledOnServer && typeof isToggled !== 'boolean') {
      setIsToggled(true);

      if (saveLastState) {
        setItem(facetToggleField, true);
      }
      return;
    }

    // make preselection on init or from localStorage
    if (
      !isToggledOnServer &&
      ((saveLastState && isToggled) || (typeof isToggled !== 'boolean' && initToggled))
    ) {
      dispatch(toggle(toggledFacet));
      setIsToggled(true);
      if (saveLastState) {
        setItem(facetToggleField, true);
      }
      return;
    }

    if (typeof isToggled === 'boolean' && isToggled !== isToggledOnServer) {
      setIsToggled(isToggledOnServer);
      if (saveLastState) {
        setItem(facetToggleField, isToggledOnServer);
      }
    }
  }, [
    dispatch,
    isVisible,
    initToggled,
    isToggled,
    isToggledOnServer,
    toggledFacet,
    saveLastState,
    facetToggleField,
  ]);

  if (!toggledFacet || !isVisible) {
    return null;
  }

  const toggleFacet = () => {
    if (saveLastState) {
      setItem(facetToggleField, !isToggled);
    }

    setIsToggled(!isToggled);
    dispatch(toggle(toggledFacet, { extra: { facetToggleJustToggled: toggledFacet.field } }));
  };

  return template.call({
    isToggled: isInverted ? !isToggled : isToggled,
    toggleFacet,
  });
};

export default FacetToggle;

/**
 * Predicate for the toggle's storage slot. Historically the slot held a
 * JSON-encoded boolean string (`'"true"'`); now it stores the boolean directly.
 * We accept both shapes to survive a session that began under the legacy encoding.
 */
function isFacetToggleStored(value: unknown): value is boolean | string {
  return typeof value === 'boolean' || value === 'true' || value === 'false';
}

function useVisible(
  fieldsForVisibility: Array<string> = [],
  toggledFacet: SelectionEntry | undefined,
): boolean {
  const facetToToggle = useSelector(createFacetSelector(toggledFacet?.field ?? ''));
  const responseTotalHits = useSelector(totalHitsSelector);
  const isToggleSelected = facetToToggle?.selection.some(
    (value) => value.term === (toggledFacet?.term ?? ''),
  );
  const isToggleMatchingAllHits =
    !isToggleSelected &&
    facetToToggle?.totalHits === responseTotalHits &&
    facetToToggle?.values.find((v) => v.term === (toggledFacet?.term ?? ''))?.hitCount === responseTotalHits;

  const responseQuery = useSelector(searchQuerySelector);
  const hideBySearchQuery = responseQuery && toggledFacet?.field === 'Universal';

  const facetsSelector = useMemo(() => createFitmentSearchResponseAllFacetsSelector(), []);
  const isAllFacetsHaveSelection = useSelector((state) => {
    const facets = facetsSelector(state);
    return fieldsForVisibility.every((field) => facets.get(field)?.selection.length);
  });

  return facetToToggle != null && !isToggleMatchingAllHits && !hideBySearchQuery && isAllFacetsHaveSelection;
}
